/**
 * ArtifactsPage — /artifacts
 * Demo page: /file-manager (library of typed items with owner, size and modified columns) on the ListPage template
 * (/tables/data-tables toolbar). Columns live in ./artifacts-list/.
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { FileText, Plus } from 'lucide-react';
import { Button, Callout, DataTable, EmptyState, FilterPills, SearchBar, Toolbar } from '../../components/ui/index.js';
import { ListPage } from '../../components/page/index.js';
import { Can } from '../../components/auth/Can.js';
import { buttonStyles } from '../../components/tailgrids/core/button.js';
import { describeQueryError } from '../../components/ui/QueryBoundary.js';
import { PAGE_HELP } from '../../help/index.js';
import { listArtifactsInfiniteOptions, searchArtifactsOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import { FilterSelect } from '../../components/ui/FilterSelect.js';
import { ARTIFACT_KINDS, getKindConfig, type ArtifactKind, type ArtifactScope } from './artifact-detail/kind-config.js';
import { MEMORY_SCOPE_LABEL, MEMORY_SCOPES } from './memory-detail/memory-scope.js';
import { ARTIFACT_COLUMNS, type ArtifactListRow } from './artifacts-list/ArtifactColumns.js';

type Visibility = 'active' | 'deleted' | 'all';

const ALL_KINDS = 'all';
const KIND_PILLS = [
  { id: ALL_KINDS, label: 'All' },
  ...ARTIFACT_KINDS.slice(0, 6).map((kind) => ({ id: kind, label: getKindConfig(kind).badgeLabel })),
];
const VISIBILITY_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'deleted', label: 'Deleted' },
  { value: 'all', label: 'Active and deleted' },
];

const NewArtifactLink: React.FC = () => (
  <Can policy="CanCreateMemoriesArtifacts">
    <Link to="/artifacts/new" className={buttonStyles({ variant: 'primary', size: 'sm' })}>
      <Plus size={14} aria-hidden="true" />
      New artifact
    </Link>
  </Can>
);

export const ArtifactsPage: React.FC = () => {
  const help = PAGE_HELP['artifacts'];
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [kind, setKind] = useState<string>(ALL_KINDS);
  const [scope, setScope] = useState<ArtifactScope | ''>('');
  const [visibility, setVisibility] = useState<Visibility>('active');

  const term = search.trim();
  const searching = term.length > 0;
  const kindQuery = kind === ALL_KINDS ? {} : { kind: kind as ArtifactKind };
  const scopeQuery = scope ? { scope } : {};

  const list = useInfiniteQuery({
    ...listArtifactsInfiniteOptions({
      query: {
        limit: 50,
        sort_by: 'updatedAt',
        sort_order: 'desc',
        include_total: true,
        view: 'full',
        include_deleted: visibility !== 'active',
        ...kindQuery,
        ...scopeQuery,
      },
    }),
    initialPageParam: {},
    getNextPageParam: (last) => (last.paging.hasMore ? (last.paging.cursors.after ?? undefined) : undefined),
    enabled: !searching,
  });
  const found = useQuery({
    ...searchArtifactsOptions({ query: { q: term, limit: 100, view: 'full', ...kindQuery, ...scopeQuery } }),
    enabled: searching,
  });

  const loaded: ArtifactListRow[] = searching
    ? (found.data?.data ?? [])
    : (list.data?.pages.flatMap((p) => p.data) ?? []);
  const rows = visibility === 'deleted' ? loaded.filter((a) => a.deletedAt !== null) : loaded;
  const total = list.data?.pages[0]?.meta.total;
  const error = searching ? found.error : list.error;
  const filtered = searching || kind !== ALL_KINDS || scope !== '' || visibility !== 'active';

  return (
    <ListPage
      title="Artifacts"
      subtitle="Plans, reports, specs, code and decisions produced by agents and people"
      helpContent={help}
      actions={<NewArtifactLink />}
      alert={
        error ? (
          <Callout
            tone="danger"
            title={describeQueryError(error, 'artifacts')}
            actions={
              <Button size="sm" variant="secondary" onClick={() => (searching ? found.refetch() : list.refetch())}>
                Retry
              </Button>
            }
          />
        ) : undefined
      }
      toolbar={
        <Toolbar
          search={<SearchBar value={search} onChange={setSearch} placeholder="Search title, content, tag…" />}
          pills={<FilterPills aria-label="Artifact type" options={KIND_PILLS} value={kind} onChange={setKind} />}
          right={
            <>
              <FilterSelect
                label="Scope"
                anyLabel="All scopes"
                value={scope}
                options={MEMORY_SCOPES.map((s) => ({ value: s, label: MEMORY_SCOPE_LABEL[s] }))}
                onChange={(value) => setScope(value as ArtifactScope | '')}
              />
              {!searching && (
                <FilterSelect
                  label="Status"
                  anyLabel="Active"
                  value={visibility === 'active' ? '' : visibility}
                  options={VISIBILITY_OPTIONS.filter((o) => o.value !== 'active')}
                  onChange={(value) => setVisibility((value || 'active') as Visibility)}
                />
              )}
            </>
          }
        />
      }
      footer={
        <div className="flex items-center justify-between gap-3 text-sm text-muted">
          <span>
            Showing {rows.length}
            {!searching && visibility === 'active' && total !== undefined ? ` of ${total}` : ''}
          </span>
          {!searching && list.hasNextPage && (
            <Button variant="secondary" loading={list.isFetchingNextPage} onClick={() => list.fetchNextPage()}>
              Load more
            </Button>
          )}
        </div>
      }
    >
      <DataTable
        aria-label="Artifacts"
        columns={ARTIFACT_COLUMNS}
        rows={rows}
        rowKey={(row) => row.id}
        loading={searching ? found.isPending : list.isPending}
        getRowAttention={(row) => (row.deletedAt ? 'warning' : undefined)}
        onRowClick={(row) => {
          if (!row.deletedAt) navigate(`/artifacts/${row.id}`);
        }}
        empty={
          <EmptyState
            icon={FileText}
            title={filtered ? 'No artifacts match' : (help?.emptyState?.title ?? 'No artifacts yet')}
            description={
              filtered
                ? 'Try a different search or clear a filter.'
                : (help?.emptyState?.description ??
                  'Agents produce artifacts as they work: plans, reports, specs and code. They collect here.')
            }
            learnItems={filtered ? undefined : help?.emptyState?.learnItems}
            action={filtered ? undefined : <NewArtifactLink />}
          />
        }
      />
    </ListPage>
  );
};
